import React from 'react';
import { Box } from '@mui/material';
import { ErrorMessage } from '../../../components/ErrorMessage';
import { useProcessingOperation } from '../hooks';
import { StatusTree } from './StatusTree';
import TemplateApi from '../../../features/template/api/templateApi';
import { ProcessingType } from '../types';

interface ProcessingContentProps {
  form: {
    from: string;
    subject: string;
    body: string;
    allowedDomains?: string[];
    senderBias?: string;
    contentBiases?: string[];
    selectedModel?: string;
  };
  processingType: ProcessingType;
  onComplete: () => void;
  onWsUrlChange: (url: string | null) => void;
}

export const ProcessingContent: React.FC<ProcessingContentProps> = ({
  form,
  processingType,
  onComplete,
  onWsUrlChange,
}) => {
  const {
    startProcessing,
    isLoading,
    error,
    taskId,
    wsUrl,
  } = useProcessingOperation({
    processingType,
    onComplete,
  });

  // Debug logging for component lifecycle
  React.useEffect(() => {
    console.log('[ProcessingContent] Mounted', {
      isLoading,
      error,
      taskId,
      wsUrl,
      form,
      processingType,
    });
    return () => {
      console.log('[ProcessingContent] Unmounted', { isLoading, error });
      // Clear WebSocket URL on unmount
      onWsUrlChange(null);
    }
  }, []);

  // Debug logging for prop changes
  React.useEffect(() => {
    console.log('[ProcessingContent] Props updated', {
      isLoading,
      error,
      taskId,
      wsUrl,
      form,
      processingType,
    });
  }, [isLoading, error, taskId, wsUrl, form, processingType]);

  // Use a ref to track if we've started processing
  const hasStartedRef = React.useRef(false);

  React.useEffect(() => {
    const initProcessing = async () => {
      if (hasStartedRef.current) {
        console.log('[ProcessingContent] Skipping initProcessing - already started');
        return;
      }
      console.log('[ProcessingContent] Starting initProcessing');
      hasStartedRef.current = true;

      try {
        console.log(`[ProcessingContent] Calling ${processingType}Template`);
        const response = processingType === 'analysis' 
          ? await TemplateApi.analyzeTemplate({
              from: form.from,
              subject: form.subject,
              body: form.body,
              allowedDomains: [],
              senderBias: '',
              contentBiases: []
            })
          : await TemplateApi.rewriteTemplate({
              from: form.from,
              subject: form.subject,
              body: form.body,
              allowedDomains: form.allowedDomains || [],
              senderBias: form.senderBias || '',
              contentBiases: form.contentBiases || [],
              selectedModel: form.selectedModel
            });

        if (response.connection?.uri) {
          onWsUrlChange(response.connection.uri);
          await startProcessing({
            taskResponse: response
          });
        }
        console.log('[ProcessingContent] startProcessing completed successfully');
      } catch (err) {
        console.log('[ProcessingContent] startProcessing failed', err);
        onComplete();
        console.error('Error during form submission:', err);
      }
    };
    initProcessing();
  }, []);

  if (error) {
    return <ErrorMessage message={error.message || 'An error occurred during processing'} />;
  }

  return (
    <Box sx={{ mt: 2 }}>
      <StatusTree />
    </Box>
  );
}; 